import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';
import type { ILocationRepository } from '@inventory/core/domain/interfaces';
import { Location } from '@inventory/core/domain/entities';

interface LocationPouchDocument extends BasePouchDocument {
    warehouseId: string;
    code: string;
    locationType: 'pallet' | 'shelf' | 'bin' | 'bulk' | 'staging';
    status: 'active' | 'inactive' | 'reserved' | 'maintenance';
    dimensions: {
        length: number;
        width: number;
        height: number;
    };
    capacity: {
        maxWeight: number;
        maxPallets: number;
    };
    restrictions: string[];
    parentId?: string;
    metadata?: Record<string, string>;
    hierarchyPath: string[];
    fullCode: string;
    createdAt: string;
    updatedAt: string;
}

export class LocationPouchDBAdapter extends BasePouchDBAdapter<Location> implements ILocationRepository {

    constructor(dbName: string) {
        super(dbName, 'location');
        this.createIndexes();
    }

    async save(entity: Location): Promise<void> {
        const hierarchyPath = await this.buildHierarchyPath(entity);
        const fullCode = await this.buildFullCode(entity, hierarchyPath);
        const now = new Date().toISOString();

        const doc: LocationPouchDocument = {
            _id: entity.id,
            type: 'location',
            version: 1,
            warehouseId: entity.warehouseId,
            code: entity.code,
            locationType: entity.type,
            status: entity.status,
            dimensions: entity.dimensions,
            capacity: entity.capacity,
            restrictions: entity.restrictions,
            parentId: entity.parentId ?? undefined,
            metadata: entity.metadata,
            hierarchyPath,
            fullCode,
            createdAt: now,
            updatedAt: now,
        };

        try {
            const existing = await this.db.get(entity.id);
            await this.db.put({ ...doc, _rev: (existing as any)._rev });
        } catch (error) {
            if ((error as any).status === 404) {
                await this.db.put(doc);
            } else {
                throw this.handleError(error as any);
            }
        }
    }

    async delete(id: string): Promise<void> {
        try {
            const doc = await this.db.get(id);
            await this.db.remove(doc);
        } catch (error) {
            console.error('Error deleting location:', error);
            throw error;
        }
    }

    protected async buildFullCode(location: Location, hierarchyPath: string[]): Promise<string> {
        if (hierarchyPath.length === 0) {
            return location.code;
        }

        const parentCodes = await Promise.all(
            hierarchyPath.map(async (id) => {
                const parent = await this.findById(id);
                return parent?.code || '';
            })
        );

        return [...parentCodes, location.code].join('-');
    }

    private async createIndexes(): Promise<void> {
        try {
            await this.db.createIndex({
                index: {
                    fields: ['type', 'warehouseId', 'hierarchyPath']
                }
            });

            await this.db.createIndex({
                index: {
                    fields: ['type', 'code', 'status']
                }
            });

            await this.db.createIndex({
                index: {
                    fields: ['type', 'fullCode']
                }
            });
        } catch (error) {
            console.error('Failed to create indexes:', error);
            throw new Error('Failed to initialize location indexes');
        }
    }

    private isError(error: unknown): error is Error {
        return error instanceof Error;
    }

    async findByWarehouse(warehouseId: string): Promise<Location[]> {
        try {
            const result = await this.db.find({
                selector: {
                    type: 'location',
                    warehouseId
                }
            });

            return result.docs.map(doc => this.fromDocument(doc as unknown as LocationPouchDocument));
        } catch (error) {
            if (this.isError(error)) {
                throw error;
            }
            throw new Error('Unknown error while finding locations by warehouse');
        }
    }

    async findChildren(parentId: string): Promise<Location[]> {
        try {
            const result = await this.db.find({
                selector: {
                    type: 'location',
                    parentId
                }
            });

            return result.docs.map(doc => this.fromDocument(doc as unknown as LocationPouchDocument));
        } catch (error) {
            if (this.isError(error)) {
                throw error;
            }
            throw new Error('Unknown error while finding child locations');
        }
    }

    async findByCode(warehouseId: string, code: string): Promise<Location | null> {
        try {
            const result = await this.db.find({
                selector: {
                    type: 'location',
                    warehouseId,
                    code
                },
                limit: 1
            });

            if (result.docs.length === 0) {
                return null;
            }

            return this.fromDocument(result.docs[0] as unknown as LocationPouchDocument);
        } catch (error) {
            if (this.isError(error)) {
                throw error;
            }
            throw new Error('Unknown error while finding location by code');
        }
    }

    async findByFullCode(warehouseId: string, fullCode: string): Promise<Location | null> {
        try {
            const result = await this.db.find({
                selector: {
                    type: 'location',
                    warehouseId,
                    fullCode
                },
                limit: 1
            });

            if (result.docs.length === 0) {
                return null;
            }

            return this.fromDocument(result.docs[0] as unknown as LocationPouchDocument);
        } catch (error) {
            if (this.isError(error)) {
                throw error;
            }
            throw new Error('Unknown error while finding location by full code');
        }
    }

    async findAvailable(warehouseId: string, locationType: string): Promise<Location[]> {
        try {
            const result = await this.db.find({
                selector: {
                    type: 'location',
                    warehouseId,
                    locationType,
                    status: 'active'
                }
            });

            return result.docs.map(doc => this.fromDocument(doc as unknown as LocationPouchDocument));
        } catch (error) {
            if (this.isError(error)) {
                throw error;
            }
            throw new Error('Unknown error while finding available locations');
        }
    }

    protected async buildHierarchyPath(location: Location): Promise<string[]> {
        const hierarchyPath: string[] = [];

        if (location.parentId) {
            const parent = await this.findById(location.parentId);
            if (parent) {
                const parentDoc = await this.db.get(parent.id);
                hierarchyPath.push(...((parentDoc as LocationPouchDocument).hierarchyPath || []), parent.id);
            }
        }

        return hierarchyPath;
    }

    protected fromDocument(doc: LocationPouchDocument): Location {
        return Location.restore({
            _id: doc._id,
            type: doc.locationType,
            version: doc.version ?? 1,
            warehouseId: doc.warehouseId,
            code: doc.code,
            // @ts-ignore — Phase 2: status/dimensions/capacity/restrictions/metadata not in current LocationSchema
            status: doc.status,
            dimensions: doc.dimensions,
            capacity: doc.capacity,
            restrictions: doc.restrictions,
            parentId: doc.parentId,
            metadata: doc.metadata,
            createdAt: new Date(doc.createdAt),
            updatedAt: new Date(doc.updatedAt)
        } as any);
    }
}